import type { PresetPermission, PresetTeam } from "@pax/api-contract/presets/v1alpha1";
import type { Item, Team } from "@pax/draft";
import { Lock } from "lucide-react";
import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/**
 * Whether you may change what each tab shows: the preset's roles decide, the same on every version,
 * and every tab asks before it offers to change anything.
 */

export interface Access {
  /** Whether you may change an item, add to it and delete it. */
  canEdit: (item: Item) => boolean;
  /** Whether your roles hold a permission, such as `publish` for early copies. */
  holds: (permission: PresetPermission) => boolean;
}

export const AccessContext = createContext<Access>({ canEdit: () => true, holds: () => true });

export const useAccess = () => use(AccessContext);

/**
 * Whether what's in an area may be changed. Where your roles don't let you, the area says no, and
 * every field, menu, switch and button in it that would change something follows: you can still
 * read, scroll, select and look, but nothing offers to change what you can't.
 */
export const EditableContext = createContext(true);

export const useEditable = () => use(EditableContext);

/**
 * Told when someone tries to change what's read only, typing, pasting or deleting in it, so the
 * banner saying why can catch their eye where they are.
 */
const ReadOnlyNudge = createContext<() => void>(() => {});

export const useReadOnlyNudge = () => use(ReadOnlyNudge);

/** Whether a key pressed alone would change text: a character, Backspace, Delete or Enter. */
export const changesText = (event: { key: string; metaKey: boolean; ctrlKey: boolean }) =>
  !event.metaKey &&
  !event.ctrlKey &&
  (event.key.length === 1 || ["Backspace", "Delete", "Enter"].includes(event.key));

/** How long the banner stays lit once someone tries to change what it's over. */
const FLASH_MS = 1200;

/** Whether the banner is lit, and what lights it: give it to `ReadOnlyNudge` over the area. */
export function useReadOnlyFlash(): [boolean, () => void] {
  const [flashing, setFlashing] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const nudge = useCallback(() => {
    setFlashing(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setFlashing(false), FLASH_MS);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [flashing, nudge];
}

/**
 * Says a tab is read only and why, plainly enough to be seen: a pill, not a caption. It lights up
 * amber when you try to change something in the tab.
 */
export function ReadOnlyBanner({
  what,
  flashing = false,
  compact = false,
}: {
  what: string;
  flashing?: boolean;
  /** As small as a section's buttons, to sit by its title; on a phone, only "Read only". */
  compact?: boolean;
}) {
  return (
    <p
      role="status"
      className={`flex w-fit max-w-full items-center rounded-full ring-1 transition-[background-color,box-shadow] duration-300 ${compact ? "h-8 shrink gap-1.5 px-3 text-xs" : "mx-auto gap-2 px-4 py-2 text-sm"} ${flashing ? "bg-amber-500/20 ring-amber-500/50" : "bg-foreground/[0.05] ring-transparent"}`}
    >
      <Lock className={`${compact ? "size-3.5" : "size-4"} shrink-0`} />
      <span className="min-w-0 truncate">
        <span className="font-medium">Read only.</span>{" "}
        <span className={`text-muted-foreground ${compact ? "max-sm:sr-only" : ""}`}>
          Your roles don't let you edit {what}.
        </span>
      </span>
    </p>
  );
}

/**
 * A tab, with the banner over it when your roles don't let you edit what it shows: at its top, in
 * the same place on every tab, kept in view whatever's scrolled beneath it. Trying to change
 * anything in it lights the banner. `what` is what you can't edit, or null when you can. What's
 * read only in it says so itself, by `EditableContext`, as each area knows what it may change.
 */
export function ReadOnlyFrame({ what, children }: { what: string | null; children: ReactNode }) {
  const [flashing, nudge] = useReadOnlyFlash();
  return (
    <div className="absolute inset-0 flex flex-col">
      {what && (
        // As much room under it as over it, so nothing that runs right to its edge, as a map does,
        // sits tight against it.
        <div className="shrink-0 px-4 py-3">
          <ReadOnlyBanner what={what} flashing={flashing} />
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        <ReadOnlyNudge value={nudge}>{children}</ReadOnlyNudge>
      </div>
    </div>
  );
}

/** The team as a draft reads who may change what, from the team the API keeps. */
export const teamOfPreset = (team: PresetTeam): Team => ({
  owner: team.owner.profileId,
  roles: team.roles,
  members: new Map(
    team.members
      .filter(member => member.joinedAt)
      .map(member => [member.profile.profileId, member.roleIds]),
  ),
});

/** Beside something your roles don't let you change, says so. Nothing beside what you can. */
export function ReadOnly({
  item,
  name,
  className = "",
}: {
  item: Item;
  name: string;
  className?: string;
}) {
  if (useAccess().canEdit(item)) return null;
  return (
    <span
      title={`Your roles don't let you edit ${name}`}
      className={`flex h-7 shrink-0 items-center gap-1 rounded-full px-2 text-xs text-muted-foreground ${className}`}
    >
      <Lock className="size-3.5" />
      Read only
    </span>
  );
}
